import {Button, TreeSelect} from 'antd';
import {DefaultOptionType} from 'antd/lib/select';
import {capitalize} from 'lodash';
import {BaseSelectRef} from 'rc-select';
import {ReactElement, UIEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Text} from '../../../../../../component/text/text';
import {Spinner} from '../../../../../../layout/spinner/spinner';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../provider/locale/localization';
import {
    useInfiniteTags,
    useMailingsTags,
    useQRCodesTags,
    useReviewsAspects,
    useTagsWithGroup,
} from '../../../../../../service/reviews/reviews-tags';
import {handleFilterOption} from '../../../../../../util/antd/select-helper';
import {onScrollInfiniteScrollContainer} from '../../../../../../util/infinite-scroll';

import * as styles from './review-tags-tree-select.scss';

type PropsType = {
    defaultValue: Array<number>;
    onConfirm: (items: Array<number | string>) => void;
    disabled?: boolean;
    isShowAutotags: boolean;
    // Review-generator tags (QR codes, mailings) are not indexed in the reviews microservice,
    // so filtering by them silently empties the ES-backed statistics widgets (BST-10553).
    // The statistics drawer therefore hides them; the reviews list keeps them.
    isShowGeneratorTags?: boolean;
};

// eslint-disable-next-line max-statements
export function ReviewTagsTreeSelect(props: PropsType): ReactElement {
    const {defaultValue, onConfirm, disabled, isShowAutotags, isShowGeneratorTags = true} = props;
    const {getLocalizedString} = useLocale();
    const {data, isFetching} = useReviewsAspects(isShowAutotags);
    const {data: qrCodeData, isLoading: qrCodeLoading} = useQRCodesTags(isShowGeneratorTags);
    const {data: mailingsData, isLoading: mailingsLoading} = useMailingsTags(isShowGeneratorTags);
    const {data: tagsWithGroup, isLoading: isTagsWithGroupLoading} = useTagsWithGroup();

    const qrCodeResult = useMemo(
        () =>
            Object.entries(qrCodeData || {}).map(([id, title]) => ({
                id: Number(id),
                title,
            })),
        [qrCodeData]
    );

    const mailingsResult = useMemo(
        () =>
            Object.entries(mailingsData || {}).map(([id, title]) => ({
                id: Number(id),
                title,
            })),
        [mailingsData]
    );

    const tagsWithGroupResult = useMemo(
        () =>
            Object.entries(tagsWithGroup || {}).map(([groupName, tagInfo]) => {
                return {
                    groupName,
                    tags: Object.entries(tagInfo || {}).map(([id, title]) => ({id: Number(id), title})),
                };
            }),
        [tagsWithGroup]
    );

    const [selectedItems, setSelectedItems] = useState<Array<number | string>>([]);
    const [open, setOpen] = useState<boolean>(false);
    const {
        data: reviewTagsList,
        isFetching: allTagsListIsInProgress,
        hasNextPage,
        fetchNextPage,
    } = useInfiniteTags({withoutFolder: true, ...(isShowGeneratorTags ? {} : {withoutRg: true})}, open);
    const tagsData = useMemo(() => {
        const allTags = reviewTagsList?.pages.flatMap((element) => element.results) || [];
        const mailingsTagIds = new Set(mailingsResult.map(({id}) => id));

        return allTags.filter((tag) => !mailingsTagIds.has(tag.id));
    }, [reviewTagsList, mailingsResult]);

    const selectRef = useRef<BaseSelectRef | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isFetching && defaultValue) {
            const foundGroups = (data || [])
                .map(({ids}) => ids.filter((id) => defaultValue.includes(id)))
                .filter((ids) => ids.length);

            const defaultSelectItems = [
                ...defaultValue.filter((number_) => !foundGroups.flat().includes(number_)),
                ...foundGroups.map((ids) => ids.join(', ')),
            ];

            setSelectedItems(defaultSelectItems);
        }
    }, [defaultValue, data, isFetching, defaultValue?.length]);

    const handleCancelClick = useCallback(() => {
        setOpen(false);
        selectRef.current?.blur();
    }, []);

    const handleConfirmClick = useCallback(() => {
        onConfirm(selectedItems);
        setOpen(false);
        selectRef.current?.blur();
    }, [onConfirm, selectedItems]);

    function onPopupScroll(event: UIEvent) {
        onScrollInfiniteScrollContainer(event, () => hasNextPage && !allTagsListIsInProgress && fetchNextPage(), 50);
    }

    const autotagsData = useMemo(
        () =>
            isShowAutotags && data && data.length > 0
                ? {
                      value: 'parent 1-0',
                      title: getLocalizedString('REVIEWS__AUTOTAGS_POPUP__HEADER'),
                      children: data.map(({ids, title}) => ({
                          value: ids.join(', '),
                          label: capitalize(title),
                      })),
                  }
                : null,
        [data, getLocalizedString, isShowAutotags]
    );

    const qrCodesTagsData = useMemo(
        () =>
            isShowGeneratorTags && qrCodeResult.length > 0
                ? {
                      value: 'parent 2-0',
                      title: getLocalizedString('REVIEW__QR_CODE__FILTER__TITLE'),
                      children: qrCodeResult.map(({id, title}) => ({
                          value: id,
                          label: title,
                      })),
                  }
                : null,
        [qrCodeResult, getLocalizedString, isShowGeneratorTags]
    );

    const mailingsTagsData = useMemo(
        () =>
            isShowGeneratorTags && mailingsResult.length > 0
                ? {
                      value: 'parent 3-0',
                      title: getLocalizedString('REVIEW__MAILINGS__FILTER__TITLE'),
                      children: mailingsResult.map(({id, title}) => ({
                          value: id,
                          label: title,
                      })),
                  }
                : null,
        [mailingsResult, getLocalizedString, isShowGeneratorTags]
    );

    const tagsWithGroupData = useMemo(
        () =>
            tagsWithGroupResult.map((group) => {
                return {
                    value: group.groupName,
                    title: group.groupName,
                    children: group.tags.map(({id, title}) => {
                        return {value: id, label: title};
                    }),
                };
            }),
        [tagsWithGroupResult]
    );

    function dropdownRender(menu: ReactElement): ReactElement {
        return (
            <div>
                <div className={styles.TagsSelect_dropdownMenu}>{menu}</div>
                <div className={styles.TagsSelect_infiniteSpinnerFaux}>
                    {allTagsListIsInProgress && (
                        <div className={styles.TagsSelect_infiniteSpinner}>
                            <Spinner size={20} spinSize="small" />
                        </div>
                    )}
                </div>

                <div className={styles.TagsSelect_dropdownFooter}>
                    <Button onClick={handleCancelClick} size="small" type="text">
                        <Locale stringKey="BUTTON__CANCEL" />
                    </Button>
                    <div>
                        {selectedItems.length > 0 && (
                            <span className={styles.TagsSelect_selectedCount}>
                                <Locale
                                    stringKey="DROPDOWN_WITH_CONFIRM_COUNT"
                                    valueMap={{count: selectedItems.length}}
                                />
                            </span>
                        )}
                        <Button onClick={handleConfirmClick} size="small" type="primary">
                            <Locale stringKey="TEXT__OK" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const treeDataChildren = useMemo(() => {
        return [
            ...(autotagsData ? [autotagsData] : []),
            ...(qrCodesTagsData ? [qrCodesTagsData] : []),
            ...(mailingsTagsData ? [mailingsTagsData] : []),
            ...tagsData.map(({id, title}) => ({
                value: id,
                label: title,
            })),
        ];
    }, [autotagsData, qrCodesTagsData, mailingsTagsData, tagsData]);

    const treeData = useMemo(() => {
        return [
            {
                value: 'parent 1',
                key: 'parent 1',
                title: getLocalizedString('TEXT__SELECT_ALL'),
                children: [
                    ...(autotagsData ? [autotagsData] : []),
                    ...(qrCodesTagsData ? [qrCodesTagsData] : []),
                    ...(mailingsTagsData ? [mailingsTagsData] : []),
                    ...tagsWithGroupData,
                    ...tagsData.map(({id, title}) => ({
                        value: id,
                        label: title,
                    })),
                ],
            },
        ];
    }, [autotagsData, qrCodesTagsData, mailingsTagsData, tagsData, tagsWithGroupData, getLocalizedString]);

    useEffect(() => {
        function handleClick({target}: MouseEvent) {
            if (!containerRef.current?.contains(target as HTMLElement) && open) {
                handleConfirmClick();
            }
        }

        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [handleConfirmClick, open]);

    const maxTagPlaceholder = useCallback(() => {
        return (
            <Locale
                stringKey="DROPDOWN_WITH_CONFIRM_COUNT"
                valueMap={{count: <Text large>{selectedItems.length}</Text>}}
            />
        );
    }, [selectedItems.length]);

    return (
        <div ref={containerRef}>
            <TreeSelect
                disabled={disabled}
                dropdownRender={dropdownRender}
                dropdownStyle={{
                    overflowY: 'auto',
                    zIndex: 9999,
                }}
                filterTreeNode={(input, filterTreeData) =>
                    handleFilterOption(input, filterTreeData as DefaultOptionType)
                }
                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                loading={
                    allTagsListIsInProgress || isFetching || qrCodeLoading || mailingsLoading || isTagsWithGroupLoading
                }
                maxTagCount={0}
                maxTagPlaceholder={maxTagPlaceholder}
                multiple
                onChange={setSelectedItems}
                onFocus={() => setOpen(true)}
                onPopupScroll={onPopupScroll}
                open={open}
                placeholder={getLocalizedString('REVIEWS__FILTER__SELECT_TAGS')}
                ref={selectRef}
                treeCheckable
                treeData={treeDataChildren.length > 0 ? treeData : []}
                treeDefaultExpandedKeys={['parent 1']}
                value={selectedItems}
                virtual={false}
            />
        </div>
    );
}
